import { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, Link, useLocation } from "react-router-dom";
import CommonLoading from "../components/loader/CommonLoading";
import ContentWithCards from "../components/shared/ContentWithCards";
import LoadMoreButton from "../components/shared/LoadMoreButton";
import SentFollowRequestsModal from "../components/modals/SentFollowRequestsModal";
import { FaUserPlus } from "react-icons/fa";
import { useToast } from "../contexts/ToastContext";

import { 
  getReceivedFollowRequestsAction, 
  clearReceivedFollowRequestsAction,
  acceptFollowRequestAction,
  denyFollowRequestAction
} from "../redux/actions/followRequestActions";

const ReceivedFollowRequests = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { success: showSuccess, error: showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [isLoadMoreLoading, setIsLoadMoreLoading] = useState(false);
  const [processingRequestId, setProcessingRequestId] = useState(null);
  const [sentRequestsModalOpen, setSentRequestsModalOpen] = useState(false);

  // TODO: Replace with actual Redux state when backend is ready
  const receivedFollowRequests = useSelector((state) => state.user?.receivedFollowRequests || []);
  const totalReceivedFollowRequests = useSelector((state) => state.user?.totalReceivedFollowRequests || 0);

  const LIMIT = 25;

  useEffect(() => {
    const fetchRequests = async () => {
      setLoading(true);
      await dispatch(getReceivedFollowRequestsAction(null, LIMIT, 0));
      setLoading(false);
    };
    fetchRequests();

    return () => {
      dispatch(clearReceivedFollowRequestsAction());
    };
  }, [dispatch, LIMIT]);

  const handleAccept = useCallback(async (requestId) => {
    if (processingRequestId) return;
    
    setProcessingRequestId(requestId);
    try {
      const result = await dispatch(acceptFollowRequestAction(requestId));
      if (result?.success) {
        showSuccess("Follow request accepted");
        await dispatch(getReceivedFollowRequestsAction(null, LIMIT, 0));
      } else {
        showError(result?.error || "Failed to accept request");
      }
    } catch (error) {
      showError("Error accepting follow request");
    } finally {
      setProcessingRequestId(null);
    }
  }, [dispatch, processingRequestId, LIMIT, showSuccess, showError]);

  const handleDeny = useCallback(async (requestId) => {
    if (processingRequestId) return;
    
    setProcessingRequestId(requestId);
    try {
      const result = await dispatch(denyFollowRequestAction(requestId));
      if (result?.success) {
        showSuccess("Follow request denied");
        await dispatch(getReceivedFollowRequestsAction(null, LIMIT, 0));
      } else {
        showError(result?.error || "Failed to deny request");
      }
    } catch (error) {
      showError("Error denying follow request");
    } finally {
      setProcessingRequestId(null);
    }
  }, [dispatch, processingRequestId, LIMIT, showSuccess, showError]);

  const handleUserClick = useCallback((user) => {
    const profileUrl = user?.profileUrl || user?._id;
    if (profileUrl) {
      navigate(`/profile/${profileUrl}`);
    }
  }, [navigate]);

  const handleLoadMore = useCallback(() => {
    setIsLoadMoreLoading(true);
    const currentLength = receivedFollowRequests?.length || 0;
    dispatch(getReceivedFollowRequestsAction(null, LIMIT, currentLength)).finally(() => {
      setIsLoadMoreLoading(false);
    });
  }, [dispatch, LIMIT, receivedFollowRequests?.length]);

  const displayedRequests = useMemo(() => {
    if (!receivedFollowRequests || receivedFollowRequests.length === 0) return [];
    
    // Group requests by requester ID and keep only the latest one for each user
    const requestsByUser = new Map();
    
    receivedFollowRequests.forEach((request) => {
      const requester = request.requester || request.user;
      if (!requester) return;
      
      const requesterId = requester._id || requester.id;
      if (!requesterId) return;
      
      const existingRequest = requestsByUser.get(requesterId);
      
      // If no existing request or this one is newer, use this one
      if (!existingRequest || new Date(request.createdAt) > new Date(existingRequest.createdAt)) {
        requestsByUser.set(requesterId, request);
      }
    });
    
    // Convert map values to array
    const uniqueRequests = Array.from(requestsByUser.values());
    
    return uniqueRequests.map((request) => {
      const requester = request.requester || request.user;
      if (!requester) return null;

      return (
        <div
          key={request._id}
          className="bg-white border rounded-md w-full shadow-2xl shadow-[#f2f5fc] relative hover:shadow-lg hover:border-primary-300 transition-all duration-300"
        >
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <div className="hover:bg-gray-50 rounded-md p-3 -m-3 transition-colors">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                {/* Left: User info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2">
                    <img
                      src={requester.avatar || "/default-avatar.png"}
                      alt={requester.name}
                      className="w-10 h-10 rounded-full object-cover flex-shrink-0 mt-0.5 cursor-pointer"
                      onClick={() => handleUserClick(requester)}
                    />
                    <div className="flex-1 min-w-0">
                      <div 
                        className="cursor-pointer mb-1"
                        onClick={() => handleUserClick(requester)}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                          <h4 className="font-semibold text-gray-800 font-heading text-sm sm:text-base truncate">
                            {requester.name}
                          </h4>
                          <span className="text-xs text-gray-500 font-roboto-flex">
                            has requested to follow you.
                          </span>
                        </div>
                      </div>
                      
                      {/* Request time */}
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs text-gray-600 font-medium font-flex-regular">
                          Requested: {new Date(request.createdAt).toLocaleString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Right: Action buttons or status */}
                <div className="flex-shrink-0 self-start sm:self-auto">
                  {request.status === "pending" && (
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                      <button
                        onClick={() => handleAccept(request._id)}
                        disabled={processingRequestId === request._id}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-green-700 hover:bg-green-800 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleDeny(request._id)}
                        disabled={processingRequestId === request._id}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-action hover:bg-action/90 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        Reject
                      </button>
                    </div>
                  )}
                  {request.status === "accepted" && (
                    <span className="px-3 py-1 text-xs font-semibold text-green-800 bg-green-200 rounded-full whitespace-nowrap">
                      Accepted
                    </span>
                  )}
                  {request.status === "denied" && (
                    <span className="px-3 py-1 text-xs font-semibold text-action bg-action/10 rounded-full whitespace-nowrap">
                      Denied
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }).filter(Boolean);
  }, [receivedFollowRequests, handleUserClick, handleAccept, handleDeny, processingRequestId]);

  const shouldShowLoadMore = useMemo(() => {
    if (!receivedFollowRequests || receivedFollowRequests.length === 0) return false;
    const currentCount = receivedFollowRequests?.length || 0;
    const totalCount = totalReceivedFollowRequests || 0;
    return currentCount < totalCount && totalCount > LIMIT;
  }, [receivedFollowRequests, totalReceivedFollowRequests, LIMIT]);

  return (
    <>
      <SentFollowRequestsModal
        isOpen={sentRequestsModalOpen}
        onClose={() => setSentRequestsModalOpen(false)}
      />
      <ContentWithCards>
        <div className="main-section bg-white border">
          <div className="p-3">
            {loading ? (
              <div className="flex items-center justify-center h-screen">
                <CommonLoading />
              </div>
            ) : (
              <div>
              {/* Title and Tabs */}
              <div className="mb-4 border-b py-2 pl-2 pr-2">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <h2 className="text-lg font-semibold text-gray-700">
                    Received Follow Requests
                  </h2>
                  
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                    {/* View Sent Requests Link */}
                    <button
                      onClick={() => setSentRequestsModalOpen(true)}
                      className="text-sm font-medium text-primary-700 hover:text-primary-800 transition-colors font-roboto-flex whitespace-nowrap"
                    >
                      View Sent Requests
                    </button>
                    
                    {/* Tabs */}
                    <div className="flex gap-2">
                      <Link
                        to="/skills-circle/received-follow-requests"
                        className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-t-lg transition-colors ${
                          location.pathname === "/skills-circle/received-follow-requests"
                            ? "bg-primary-700 text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        Follow
                      </Link>
                      <Link
                        to="/skills-circle/received-profile-requests"
                        className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-t-lg transition-colors ${
                          location.pathname === "/skills-circle/received-profile-requests"
                            ? "bg-primary-700 text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        MyST Profile
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

                {receivedFollowRequests?.length > 0 ? (
                  <>
                    <div className="grid grid-cols-1 gap-3 items-start px-2">
                      {displayedRequests}
                    </div>
                    
                    {shouldShowLoadMore && (
                      <LoadMoreButton
                        onClick={handleLoadMore}
                        isLoading={isLoadMoreLoading}
                        text="Load More Requests"
                      />
                    )}
                  </>
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                    <FaUserPlus className="text-4xl text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 font-roboto-flex text-lg mb-2">No follow requests</p>
                    <p className="text-gray-400 font-roboto-flex text-sm">
                      You don't have any pending follow requests at the moment.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </ContentWithCards>
    </>
  );
};

export default ReceivedFollowRequests;
